import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '../features/auth/authApi';
import type { AuthResponse, CurrentUser, LoginRequest, SignInOutcome } from '../features/auth/authTypes';
import { ApiError } from '../lib/apiError';
import {
  clearTokens,
  getRefreshToken,
  setAccessToken,
  setAuthLostHandler,
  setRefreshToken,
} from '../lib/api';

interface AuthContextValue {
  user: CurrentUser | null;
  /** True until the initial session-restore attempt settles. Gate routing on this. */
  initializing: boolean;
  signingIn: boolean;
  /**
   * Signs in. Resolves to an OUTCOME, because a correct password does not always produce a
   * session: an account that requires a PIN gets a challenge instead, and the caller is what
   * knows how to ask for one.
   *
   * A session in the outcome has already been adopted — the caller does not have to.
   */
  login: (request: LoginRequest) => Promise<SignInOutcome>;
  /** Adopts a session the PIN leg produced. The same three writes `login` makes. */
  adoptSession: (session: AuthResponse) => void;
  logout: () => Promise<void>;
  /** Re-reads /auth/me. Call after anything that can change the caller's own permissions. */
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [signingIn, setSigningIn] = useState(false);

  /**
   * Permission lookups happen on every gated element on every render. A Set makes each one
   * O(1) instead of a linear scan of an array that can hold hundreds of keys.
   */
  const permissionSet = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions],
  );

  const clearSession = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  // Lets lib/api tear down app state when a refresh finally fails.
  useEffect(() => {
    setAuthLostHandler(clearSession);
    return () => setAuthLostHandler(null);
  }, [clearSession]);

  // Restore a session on load. A stored refresh token is exchanged by the api layer on the
  // first 401, so simply calling /auth/me is enough to find out whether we are signed in.
  const restoreAttempted = useRef(false);

  useEffect(() => {
    if (restoreAttempted.current) {
      return;
    }

    restoreAttempted.current = true;

    if (!getRefreshToken()) {
      setInitializing(false);
      return;
    }

    // Deliberately NOT cancelled on unmount. StrictMode mounts, unmounts, and remounts in
    // development; aborting here made the in-flight restore reject, which the catch below
    // read as an auth failure and used to discard a perfectly good refresh token — signing
    // the user out on every reload. The ref above already prevents a duplicate request.
    authApi
      .me()
      .then(setUser)
      .catch((caught: unknown) => {
        // Only a genuine auth rejection ends the session. A network blip or a 500 leaves
        // the token alone so the next attempt can still succeed.
        const status = caught instanceof ApiError ? caught.status : 0;

        if (status === 401 || status === 403) {
          clearSession();
        }
      })
      .finally(() => setInitializing(false));
  }, [clearSession]);

  /*
    One place that turns a session into a signed-in state.

    It is shared rather than copied because there are now TWO ways to arrive at one — the
    password alone, and the password followed by a PIN — and a second copy is where one of them
    starts forgetting to store the refresh token.
  */
  const adoptSession = useCallback((session: AuthResponse) => {
    setAccessToken(session.accessToken);
    setRefreshToken(session.refreshToken);
    setUser(session.user);
  }, []);

  const login = useCallback(async (request: LoginRequest) => {
    setSigningIn(true);

    try {
      const outcome = await authApi.login(request);

      // A challenge carries no token, so there is nothing to adopt and the caller is handed the
      // outcome to ask the second leg.
      if (outcome.session) {
        adoptSession(outcome.session);
      }

      return outcome;
    } finally {
      setSigningIn(false);
    }
  }, [adoptSession]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout({ refreshToken: getRefreshToken() });
    } catch {
      // Signing out locally must succeed even if the server call does not — otherwise a
      // network blip leaves the user stuck in a session they asked to end.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    try {
      setUser(await authApi.me());
    } catch {
      clearSession();
    }
  }, [clearSession]);

  const hasPermission = useCallback(
    (permission: string) => permissionSet.has(permission),
    [permissionSet],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) => permissions.some((p) => permissionSet.has(p)),
    [permissionSet],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initializing,
      signingIn,
      login,
      adoptSession,
      logout,
      refreshUser,
      hasPermission,
      hasAnyPermission,
    }),
    [user, initializing, signingIn, login, adoptSession, logout, refreshUser, hasPermission, hasAnyPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>.');
  }

  return context;
}

/** Convenience hook for a single key, matching <PermissionGate>'s semantics. */
export function usePermission(permission: string): boolean {
  return useAuth().hasPermission(permission);
}
